// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './UserSecurity.module.scss';
import { useTranslation } from 'react-i18next';
import { IconEditIC } from 'components/atoms/icons/IconEditIC';
import { useEffect, useMemo, useState } from 'react';
import UserFormModal, {
  renderMFATooltip,
  UserInput,
} from 'components/organisms/UserFormModal';
import Toggle from 'components/atoms/Toggle';
import CustomPagination from 'components/atoms/CustomPagination';
import { Account, ResponseAccount } from 'util/accountManagementTypes';
import { toast } from 'react-toastify';
import Popover from 'components/atoms/Popover';
import { IconInfo } from 'components/atoms/icons/IconInfo';
import {
  createUser,
  deleteUser,
  getUsers,
  updateUser,
} from 'apis/operator_site/siteApi';
import { OperatorType, RoleType, ScaleType } from 'util/Enums';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { IconDelete } from 'components/atoms/icons/IconDelete';
import DeleteItemDarkModal from 'components/molecules/DeleteItemDarkModal';
import { IconClose } from 'components/atoms/icons/IconClose';
import _snakeCase from 'lodash/snakeCase';
import TableFilterMultiSelect from 'components/molecules/TableFilterMultiSelect';
import { Option } from 'util/Types';

const RECORDS_OF_PAGE = 20;

type ActionPermissions = {
  operatorType: OperatorType;
  role: RoleType;
}[];

const DELETE_PERMISSIONS: ActionPermissions = [
  { operatorType: OperatorType.Collects, role: RoleType.AdminOperator },
  { operatorType: OperatorType.Collects, role: RoleType.AdminSite },
];

const ADD_PERMISSIONS: ActionPermissions = [
  { operatorType: OperatorType.Collects, role: RoleType.AdminOperator },
  { operatorType: OperatorType.Collects, role: RoleType.AdminSite },
];

const UserSecurityList = () => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState<Account>();
  const [data, setData] = useState<ResponseAccount>({
    page: 1,
    total: 0,
    users: [],
  });
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState<string>();
  const pageCount = Math.ceil(data.total / RECORDS_OF_PAGE);
  const user = useSelector(selectUser);
  const [filter, setFilter] = useState<{
    roles?: RoleType[];
  }>({});

  const fetchUsers = async (page?: number) => {
    await getUsers({
      params: {
        page: page || data.page,
        take: RECORDS_OF_PAGE,
        type: filter.roles,
      },
      successCallback: (res) => {
        setData(res);
      },
    });
  };

  useEffect(() => {
    fetchUsers(1);
  }, [filter]);

  const roleFilterOptions: Option[] = useMemo(() => {
    const getRoles = () => {
      if (
        user.operatorType === OperatorType.Emissions &&
        user.role === RoleType.AdminSite
      ) {
        return [RoleType.AdminSite, RoleType.AdminTenant, RoleType.UserNormal];
      }

      return [];
    };

    return getRoles().map((role) => ({
      value: role,
      label: t(`common.roles.${_snakeCase(role)}`),
    }));
  }, [user]);

  const canViewScaleSettings = useMemo(() => {
    if (user.operatorType === OperatorType.Compressions) {
      return true;
    }

    if (user.operatorType === OperatorType.Emissions) {
      return (
        user.role &&
        [
          RoleType.AdminOperator,
          RoleType.AdminSite,
          RoleType.UserNormal,
        ].includes(user.role)
      );
    }

    return false;
  }, [user]);

  const canDeleteUser = useMemo(
    () =>
      DELETE_PERMISSIONS.some(
        (permission) =>
          permission.operatorType === user.operatorType &&
          permission.role === user.role
      ),
    [user]
  );

  const canAddUser = useMemo(
    () =>
      ADD_PERMISSIONS.some(
        (permission) =>
          permission.operatorType === user.operatorType &&
          permission.role === user.role
      ),
    [user]
  );

  const handlePageClick = (selectedItem: { selected: number }) => {
    fetchUsers(selectedItem.selected + 1);
  };

  const handleSubmit = (form: UserInput, id: number | string) => {
    const isEdit = !!id;

    const fn = isEdit ? updateUser : createUser;

    fn({
      form,
      successCallback: () => {
        fetchUsers(data.page);
        setUserData(undefined);
        toast.success(t(`messages.${isEdit ? 'M_011' : 'M_010'}`));
      },
    });
  };

  const handleDeleteUser = (id: string) => {
    if (!canDeleteUser || id.toString() === user.userId.toString()) {
      return;
    }

    deleteUser({
      id,
      successCallback: () => {
        fetchUsers(data.page);
        toast.success(t('messages.M_012'));
        setConfirmDeleteUserId(undefined);
      },
    });
  };

  return (
    <div className={styles.userSecurity}>
      <div className={styles.userSecurityContent}>
        <div className={styles.title}>
          <div
            className={styles.countItem}
            dangerouslySetInnerHTML={{
              __html:
                t('common.page', {
                  page: data?.total || 0,
                }) ?? '',
            }}
          />

          {canAddUser && (
            <button
              className={styles.btnAdd}
              onClick={() => setUserData({} as Account)}
            >
              {t('user_security.add_user')}
            </button>
          )}
        </div>
        <div className={styles.table}>
          <table id={styles.userSecurityTable}>
            <thead>
              <tr>
                <th className={styles.userName}>
                  {t('user_security.label_user_name')}
                </th>
                <th className={styles.userId}>{t('user_security.label_id')}</th>
                <th className={styles.userRole}>
                  <TableFilterMultiSelect
                    label={t('user_security.label_role')}
                    options={roleFilterOptions}
                    values={filter.roles}
                    onChange={(values) => {
                      setFilter((prev) => ({
                        ...prev,
                        roles: values as RoleType[] | undefined,
                      }));
                    }}
                  />
                </th>
                {canViewScaleSettings && (
                  <>
                    <th className={styles.scaleType}>
                      {t('user_security.label_scale_type')}
                    </th>
                    <th className={styles.scaleModel}>
                      {t('user_security.label_scale_model')}
                    </th>
                  </>
                )}
                <th className={styles.email}>
                  {t('user_security.label_email')}
                </th>
                <th className={styles.mfa}>
                  <div className={styles.mfaLabel}>
                    {t('user_security.label_mfa')}

                    <Popover
                      content={renderMFATooltip(t)}
                      arrowContainerConfigs={{
                        arrowColor: '#4d4d4d',
                      }}
                      popUpStyle={{
                        background: '#4d4d4d',
                        maxWidth: '500px',
                        whiteSpace: 'pre-line',
                      }}
                      containerStyle={{
                        zIndex: '100000',
                      }}
                    >
                      <span>
                        <IconInfo />
                      </span>
                    </Popover>
                  </div>
                </th>
                <th className={styles.actionField}>
                  {t('user_security.label_action')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.users && data.users.length > 0 ? (
                data.users.map((item, i) => {
                  return (
                    <tr key={i}>
                      <td className={styles.userName}>
                        <p>{item.name}</p>
                      </td>
                      <td className={styles.userId}>
                        <p>{item.id}</p>
                      </td>
                      <td className={styles.userRole}>
                        <p>{t(`common.roles.${_snakeCase(item.type)}`)}</p>
                      </td>
                      {canViewScaleSettings && (
                        <>
                          <td className={styles.scaleType}>
                            <p>
                              {item.scalesType
                                ? t(
                                    `user_security.scale_model.${item.scalesType.toLowerCase()}`
                                  )
                                : ''}
                            </p>
                          </td>
                          <td className={styles.scaleModel}>
                            <p>
                              {item.scalesType &&
                              item.scalesType !== ScaleType.NONE &&
                              item.scalesModel == null
                                ? t('user_security.unidentified_scale_model')
                                : item.scalesModel?.name}
                            </p>
                          </td>
                        </>
                      )}
                      <td className={styles.email}>
                        <p>{item.email}</p>
                      </td>
                      <td className={styles.mfa}>
                        <Toggle value={item.mfaEnable} disabled isDarkMode />
                      </td>
                      <td className={styles.actionField1}>
                        <div className={styles.contentAction}>
                          <div
                            className={styles.actionEdit}
                            onClick={() => {
                              setUserData(item);
                            }}
                          >
                            <IconEditIC />
                          </div>
                          {item.id.toString() !== user.userId.toString() &&
                            canDeleteUser && (
                              <div
                                className={styles.actionDelete}
                                onClick={() => {
                                  setConfirmDeleteUserId(item.id);
                                }}
                              >
                                <IconDelete />
                              </div>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={canViewScaleSettings ? 7 : 5}
                    className={styles.msgError}
                  >
                    {t('messages.M_008')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.Pagination}>
          {pageCount > 1 && (
            <CustomPagination
              pageCount={pageCount}
              onPageChange={handlePageClick}
              forcePage={Math.max(data.page - 1, 0)}
            />
          )}
        </div>
      </div>

      {userData && (
        <UserFormModal
          onClose={() => {
            setUserData(undefined);
          }}
          userData={userData}
          onSubmit={(data, id) => handleSubmit(data, id)}
          creatable={canAddUser}
        />
      )}

      {confirmDeleteUserId && (
        <DeleteItemDarkModal
          onClose={() => {
            setConfirmDeleteUserId(undefined);
          }}
          onHandleDelete={() => handleDeleteUser(confirmDeleteUserId)}
          content={t('user_security.confirm_delete')}
          closeModalIcon={<IconClose />}
          deleteBtnLabel={t('user_security.deleteBtnLabel') || ''}
        />
      )}
    </div>
  );
};

export default UserSecurityList;
